'use client';
import { type Notification, type UnifiedMessage } from '@/lib/api';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnnouncementItem } from './announcement';
import { SystemItem } from './system';
import { TransactionItem } from './transaction';
import { NormalItem } from './normal';
import { InteractionsItem } from './interactions';
import { ScheduleCancelledItem } from './schedule-cancelled';
import { ContentModerationItem } from './content-moderation';
import { api } from '@/lib/api';
import { useState, useEffect } from 'react';
import { chainList } from '@/app/const/chain';
import dayjs from 'dayjs';
import { Pin } from 'lucide-react';
import { getCategoryTag } from './category';
import { useUserInfo } from '@/app/stores/useUserStore';

export function NotificationsItem({
  notification,
  isDropdown = false,
  openNewTab = false,
  className,
  activeTab,
  onRemove,
  onItemClick,
}: {
  notification: Notification;
  isDropdown?: boolean;
  openNewTab?: boolean;
  className?: string;
  activeTab?: string;
  onRemove?: (id: string | number) => void;
  onItemClick?: () => void;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const userInfo = useUserInfo();
  const [isSee, setIsSee] = useState(notification.isSee === 1);
  const [isRemoving, setIsRemoving] = useState(false);
  const isTodayCreateTime = notification.createTime ? dayjs(notification.createTime).isSame(dayjs(), 'day') : false;
  // 同步 notification.isSee 的变化
  useEffect(() => {
    setIsSee(notification.isSee === 1);
  }, [notification.isSee]);

  // tag 节点（全部 tab 下显示）
  const categoryTagNode =
    activeTab === 'all' || activeTab === 'notifications'
      ? (() => {
          const tag = getCategoryTag(notification.messageType, t);
          if (!tag) return null;
          return (
            <>
              <span className="text-xs px-1.5 py-0.5 rounded bg-blue-400/10 text-blue-400 whitespace-nowrap">{tag.label}</span>
              {notification.messageType === 15 && isTodayCreateTime && (
                <span className="text-xs px-1.5 py-0.5 rounded text-orange-400 bg-orange-400/10 inline-flex items-center gap-0.5 whitespace-nowrap">
                  <Pin className="size-3" />
                  {t('header.pinned')}
                </span>
              )}
            </>
          );
        })()
      : null;

  // 这里展示分类和Category不一致
  const renderContent = () => {
    if (notification.messageType === 15) {
      return (
        <AnnouncementItem announcement={notification as unknown as UnifiedMessage} isDropdown={isDropdown}>
          {categoryTagNode}
        </AnnouncementItem>
      );
    }
    if ([8, 14, 18].includes(notification.messageType)) {
      return (
        <SystemItem system={notification as unknown as UnifiedMessage} isDropdown={isDropdown}>
          {categoryTagNode}
        </SystemItem>
      );
    }
    if ([9, 10, 16, 17].includes(notification.messageType)) {
      return (
        <TransactionItem transaction={notification as unknown as UnifiedMessage} isDropdown={isDropdown}>
          {categoryTagNode}
        </TransactionItem>
      );
    }
    if ([1, 2, 3, 4].includes(notification.messageType)) {
      return (
        <InteractionsItem notification={notification} isDropdown={isDropdown}>
          {categoryTagNode}
        </InteractionsItem>
      );
    }
    /* if (notification.messageType === 19) {
            return <ReservationItem message={notification as unknown as UnifiedMessage} isDropdown={isDropdown}>{categoryTagNode}</ReservationItem>
        } */
    if (notification.messageType === 20) {
      return (
        <ScheduleCancelledItem message={notification as unknown as UnifiedMessage} isDropdown={isDropdown}>
          {categoryTagNode}
        </ScheduleCancelledItem>
      );
    }
    if (notification.messageType === 25) {
      return (
        <ContentModerationItem message={notification as unknown as UnifiedMessage} isDropdown={isDropdown}>
          {categoryTagNode}
        </ContentModerationItem>
      );
    }
    return (
      <NormalItem notification={notification} isDropdown={isDropdown}>
        {categoryTagNode}
      </NormalItem>
    );
  };

  const handleJump = () => {
    const item = notification as unknown as UnifiedMessage;
    // messageType 1(回复)/2(点赞)/4(分享) → 跳转到关联帖子
    if ([1, 2, 4].includes(item.messageType)) {
      const talkId = notification.detailData?.relatedTalk?.talkId;
      if (talkId) {
        if (openNewTab) {
          window.open(`/square/detail/${talkId}`, '_blank');
        } else {
          navigate(`/square/detail/${talkId}`);
        }
      }
    }
    // messageType 3(关注) → 跳转到粉丝列表
    else if (item.messageType === 3) {
      const userId = notification.detailData?.byUser?.userId;
      if (userId) {
        if (openNewTab) {
          window.open(`/${userInfo?.handle}/follow`, '_blank');
        } else {
          navigate(`/${userInfo?.handle}/follow`);
        }
      }
    } else if (item.messageType === 5) {
      const userId = notification.detailData?.byUser?.userId;
      if (userId) {
        if (openNewTab) {
          window.open(`/livecenter/earnings`, '_blank');
        } else {
          navigate(`/livecenter/earnings`);
        }
      }
    } else if (item.messageType === 8) {
      if (openNewTab) {
        window.open(`/${userInfo?.handle}/feedback?feedbackId=${item.variables.feedbackId}`, '_blank');
      } else {
        navigate(`/${userInfo?.handle}/feedback?feedbackId=${item.variables.feedbackId}`);
      }
    } else if ([9, 10].includes(item.messageType)) {
      if (openNewTab) {
        window.open(`/perps?token=${item.variables.symbol}&tab=positions`, '_blank');
      } else {
        navigate(`/perps?token=${item.variables.symbol}&tab=positions`);
      }
    } else if (item.messageType === 14) {
      if (openNewTab) {
        window.open(`/live/detail?id=${item.variables.roomId}`, '_blank');
      } else {
        navigate(`/live/detail?id=${item.variables.roomId}`);
      }
    } else if (item.messageType === 15) {
      const url = `/announcement/detail?id=${item.variables.announcementId}&from=notifications`;
      if (openNewTab) {
        window.open(url, '_blank');
      } else {
        navigate(url);
      }
    } else if (item.messageType === 16) {
      if (openNewTab) {
        window.open('/portfolio/spot?tab=tradeHistory', '_blank');
      } else {
        navigate('/portfolio/spot?tab=tradeHistory');
      }
    } else if (item.messageType === 17) {
      if (openNewTab) {
        window.open('/swap', '_blank');
      } else {
        navigate('/swap');
      }
    } else if (item.messageType === 18) {
      const selectedChain = chainList.find(c => c.chainId === item.variables.chainId);
      window.open(selectedChain?.tradeDetailUrl + item.variables.txHash, '_blank');
    } else if (item.messageType === 20) {
      const streamerHandle = (item.variables as any).streamerHandle;
      if (openNewTab) {
        window.open(`/${streamerHandle}`, '_blank');
      } else {
        navigate(`/${streamerHandle}`);
      }
    } else if (item.messageType === 25) {
      const talkId = item.variables?.relId || '';
      if (talkId) {
        if (openNewTab) {
          window.open(`/square/detail/${talkId}`, '_blank');
        } else {
          navigate(`/square/detail/${talkId}`);
        }
      }
    }
  };
  // 标记已读，和app端保持一致全都点击已读
  const handleClick = async () => {
    // 跳转前关闭外部下拉/弹层
    onItemClick?.();
    try {
      // interactions 分类且有聚合消息时，使用批量已读接口（每批最多100条）
      if (notification.category === 'interactions' && notification.groupMessageIds?.length && notification.groupMessageIds?.length > 1) {
        const allMessages = notification.groupMessageIds.map(id => ({
          messageId: id,
          messageType: notification.messageType,
        }));
        const BATCH_SIZE = 100;
        let allSuccess = true;
        for (let i = 0; i < allMessages.length; i += BATCH_SIZE) {
          const batch = allMessages.slice(i, i + BATCH_SIZE);
          const res = await api.user.batchMarkAsReadV2({ messages: batch });
          if (res.code !== 200) {
            allSuccess = false;
          }
        }
        if (allSuccess) {
          setIsSee(true);
        }
      } else {
        // 调用 V2 标记已读接口
        const res = await api.user.markAsReadV2({
          messageId: notification.id,
          messageType: notification.messageType,
        });
        if (res.code === 200) {
          setIsSee(true);
        }
      }
    } catch (err) {
      // 错误处理：即使接口失败也跳转
      console.error('标记已读失败:', err);
    } finally {
      // 如果 openNewTab 为 false，则从列表中移除（带动画）
      if (!openNewTab && onRemove) {
        // 触发移除动画
        setIsRemoving(true);
        // 先跳转
        handleJump();
        // 等待动画完成后从列表中移除
        setTimeout(() => {
          onRemove(notification.id);
        }, 300); // 动画时长 300ms
      } else {
        // 如果 openNewTab 为 true，直接跳转
        handleJump();
      }
    }
  };

  return (
    <div
      key={notification.id}
      className={`flex gap-3 py-3 border-b border-button-border/50 last:border-b-0 px-3 -mx-2 transition-all duration-300 relative hover:bg-secondary-hover ${className} cursor-pointer ${isRemoving ? 'opacity-0 -translate-x-4 pointer-events-none max-h-0 overflow-hidden' : 'opacity-100 translate-x-0'}`}
      onClick={handleClick}
    >
      {openNewTab && !isSee && (
        <div
          className="size-1.5 rounded-full bg-green-500 absolute top-1/2 -translate-y-1/2 left-0.5"
          style={{
            animation: 'breathing 2s ease-in-out infinite',
          }}
        />
      )}
      {renderContent()}
    </div>
  );
}
